import {RefObject} from 'react';
import {throttle} from '@/common/helpers/utils/throttle';

/**
 * A set of tools for the implementation of the checking and observing the intersection of a block in a viewport
 * or approaching it for its advance loading
 */
export function useLazyLoadingIntersectionChecker({
	ref,
	advance = 0,
}: {
	ref: RefObject<HTMLElement>;
	isActive?: boolean;

	/** Offset of the block to a viewport rect edge at which it is considered visible */
	advance?: number;
}) {
	const check = () => {
		return ref.current!.getBoundingClientRect().top - window.innerHeight < advance;
	};

	const observe = (callback: () => void) => {
		const handleCheckThrottled = throttle(() => {
			if (check()) {
				callback();
				window.removeEventListener('scroll', handleCheckThrottled);
			}
		}, 120);

		window.addEventListener('scroll', handleCheckThrottled);
		window.addEventListener('resize', handleCheckThrottled);

		// Dispose method to unobserve
		return () => {
			window.removeEventListener('scroll', handleCheckThrottled);
			window.removeEventListener('resize', handleCheckThrottled);
			handleCheckThrottled.cancel();
		};
	};

	return {
		check,
		observe,
	};
}

useLazyLoadingIntersectionChecker.displayName = 'useLazyLoadingIntersectionChecker';
